// Edge factory functions
// Creates edge instances with sensible defaults and unique IDs

import type { PipeEdge, CompressiblePipeEdge, DuctEdge } from '../edges';


// ============================================================================
// NEW FACTORY FUNCTION (ADR-0009 naming conventions)
// ============================================================================

/**
 * Creates a pipe edge with provided ID and connection endpoints
 * @param id - The unique identifier for the pipe
 * @param sourceId - ID of the source vertex
 * @param targetId - ID of the target vertex
 * @param overrides - Optional partial overrides (id, type, and connection cannot be overridden)
 * @returns A complete PipeEdge object
 */
export function createPipe(
  id: string,
  sourceId: string,
  targetId: string,
  overrides?: Partial<Omit<PipeEdge, 'id' | 'type' | 'connection'>>
): PipeEdge {
  const defaults: PipeEdge = {
    id,
    type: 'pipe',
    name: `Pipe ${id}`,
    connection: {
      source_id: sourceId,
      target_id: targetId,
    },
    fluid_zone_id: '', // Empty until user assigns a fluid zone
    pipe_specification_id: '', // Empty until user assigns a pipe specification
    pipe_size_id: '', // Empty until user assigns a pipe size
    length: { value: 0, unit: 'm' },
    is_closed: false,
    is_oneway: false,
    diagram: { points: [] },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge diagram to preserve structure when partially overriding
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id, type, and connection cannot be overridden
    id,
    type: 'pipe',
    connection: {
      source_id: sourceId,
      target_id: targetId,
    },
  };
}

// ============================================================================
// v2 EDGE FACTORY FUNCTIONS
// ============================================================================

/**
 * Creates a compressible pipe edge with provided ID and connection endpoints
 * Same fields as pipe but for compressible flow analysis
 */
export function createCompressiblePipe(
  id: string,
  sourceId: string,
  targetId: string,
  overrides?: Partial<Omit<CompressiblePipeEdge, 'id' | 'type' | 'connection'>>
): CompressiblePipeEdge {
  const defaults: CompressiblePipeEdge = {
    id,
    type: 'compressible_pipe',
    name: `Compressible Pipe ${id}`,
    connection: {
      source_id: sourceId,
      target_id: targetId,
    },
    fluid_zone_id: 'default_zone',
    pipe_specification_id: 'default_spec',
    pipe_size_id: 'default_size',
    length: { value: 0, unit: 'm' },
    is_closed: false,
    is_oneway: false,
    diagram: { points: [] },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'compressible_pipe',
    connection: {
      source_id: sourceId,
      target_id: targetId,
    },
  };
}

/**
 * Creates a duct edge with provided ID and connection endpoints
 */
export function createDuct(
  id: string,
  sourceId: string,
  targetId: string,
  overrides?: Partial<Omit<DuctEdge, 'id' | 'type' | 'connection'>>
): DuctEdge {
  const defaults: DuctEdge = {
    id,
    type: 'duct',
    name: `Duct ${id}`,
    connection: {
      source_id: sourceId,
      target_id: targetId,
    },
    fluid_zone_id: 'default_zone',
    length: { value: 0, unit: 'm' },
    geometry: { shape: 'circular', diameter: { value: 0, unit: 'm' } },
    roughness: { value: 0, unit: 'mm' },
    loss_coefficient: 0,
    is_closed: false,
    diagram: { points: [] },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'duct',
    connection: {
      source_id: sourceId,
      target_id: targetId,
    },
  };
}


/**
 * The model's default pipe connectivity fields for a NEWLY-drawn pipe: the first
 * fluid zone and the first pipe size. A Connect-authored custom model has exactly
 * one of each, so these resolve unambiguously; a multi-zone model gets the first
 * (the user can reassign in the property panel). Returns '' when the model has
 * none — the calc engine then surfaces a clear "assign a fluid zone/size" error.
 *
 * `length` is deliberately NOT defaulted — it has no physically-safe default (a
 * wrong length silently kills the friction term), so the UI prompts for it.
 */
export function defaultPipeFields(system: {
  fluid_zones?: { id: string }[];
  pipe_specifications?: { id: string; sizes?: { id: string }[] }[];
}): {
  fluid_zone_id: string;
  pipe_specification_id: string;
  pipe_size_id: string;
  is_closed: boolean;
  is_oneway: boolean;
} {
  const spec = system.pipe_specifications?.[0];
  return {
    fluid_zone_id: system.fluid_zones?.[0]?.id ?? '',
    pipe_specification_id: spec?.id ?? '',
    pipe_size_id: spec?.sizes?.[0]?.id ?? '',
    // ⚠️ REQUIRED by PipeEdgeSchema (`z.boolean()`, not optional) — `createPipe` sets
    // them, but every hand-built edge that spreads this helper did not, so app-drawn
    // pipes were schema-invalid. Nothing rejected them on save, so the breakage only
    // surfaced downstream: Save to Library returned HTTP 400 "Invalid request body"
    // because the snapshot re-validates every edge.
    is_closed: false,
    is_oneway: false,
  };
}
